/**
 * @fileoverview Promotional Cover Screen Component for Buzzer Battle ("De barrio en barrio") on stadium LED screens (/screen).
 * Renders high-impact promotional branding with sponsor/club logos, floating title animations,
 * dynamic theme backgrounds (color, image, video), and contrast-safe typography.
 * Implements a Zero-Canvas architecture with GPU Layer Compositing (0% CPU usage in idle state, 100% free of QR codes).
 * 
 * @module components/Screen/Sections/BuzzerBattle/BuzzerBattleCover
 */

import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { GetRealtimeState, GetThemes, GetTheme } from '../../../../utilities/BL/BuzzerBattle';

/**
 * Default fallback colors and configuration when theme is not provided.
 * @constant {Object}
 */
const DEFAULT_COVER_CONFIGURATION = {
    coverTitle: 'DE BARRIO EN BARRIO',
    coverBackgroundType: 'image',
    coverBackgroundColor: '#0a0a14',
    coverBackgroundUrl: '',
    coverTextColor: '#ffffff',
    brandLogoUrl: '',
    sponsorLogoUrl: ''
};

/**
 * BuzzerBattleCover Component
 * 
 * @function BuzzerBattleCover
 * @param {Object} props - Component properties.
 * @param {Object} [props.payload] - WebSocket payload or initial data from parent Screen router.
 * @returns {React.ReactElement} The rendered stadium cover view.
 */
export const BuzzerBattleCover = ({ payload }) => {
    // =========================================================================
    // 1. ESTADO DEL TEMA VISUAL ACTIVO
    // =========================================================================
    const [activeThemeData, setActiveThemeData] = useState(null);
    const [isLoadingThemeInformation, setIsLoadingThemeInformation] = useState(false);

    /**
     * Resolves media file paths into fully qualified absolute URLs.
     * 
     * @function resolveMediaUrl
     * @param {string|null} mediaPath - Relative or absolute media location.
     * @returns {string} Fully resolved media URL.
     */
    const resolveMediaUrl = useCallback((mediaPath) => {
        if (!mediaPath) return '';
        if (mediaPath.startsWith('http://') || mediaPath.startsWith('https://') || mediaPath.startsWith('blob:')) {
            return mediaPath;
        }
        const apiBaseEndpointUrl = (process.env.REACT_APP_API_URL || '').replace('/api/', '/').replace('/api', '/');
        const sanitizedMediaPath = mediaPath.startsWith('/') ? mediaPath.substring(1) : mediaPath;
        return `${apiBaseEndpointUrl}${sanitizedMediaPath}`;
    }, []);

    /**
     * Fetches the active theme from the backend API if not already present in the incoming payload.
     * 
     * @async
     * @function fetchActiveThemeConfiguration
     */
    const fetchActiveThemeConfiguration = useCallback(async () => {
        setIsLoadingThemeInformation(true);
        try {
            const realtimeResponse = await GetRealtimeState();
            if (realtimeResponse.success && realtimeResponse.data) {
                const roomState = realtimeResponse.data;
                if (roomState.currentTheme) {
                    setActiveThemeData(roomState.currentTheme);
                    return;
                } else if (roomState.themeId) {
                    const themeResponse = await GetTheme(roomState.themeId);
                    if (themeResponse.success && themeResponse.data) {
                        setActiveThemeData(themeResponse.data);
                        return;
                    }
                }
            }

            // Fallback: fetch list of themes
            const themesResponse = await GetThemes();
            if (themesResponse.success && Array.isArray(themesResponse.data) && themesResponse.data.length > 0) {
                setActiveThemeData(themesResponse.data[0]);
            }
        } catch (themeFetchError) {
            // Non-blocking fallback to default configuration
        } finally {
            setIsLoadingThemeInformation(false);
        }
    }, []);

    // Synchronize theme when payload changes or trigger initial fetch
    useEffect(() => {
        if (payload) {
            const incomingThemeData = payload.state?.currentTheme ||
                payload.data?.currentTheme ||
                payload.payload?.currentTheme ||
                payload.theme ||
                payload.data?.theme;

            if (incomingThemeData) {
                setActiveThemeData(incomingThemeData);
                return;
            }
        }

        if (!activeThemeData) {
            fetchActiveThemeConfiguration();
        }
    }, [payload, activeThemeData, fetchActiveThemeConfiguration]);

    // =========================================================================
    // 2. EXTRACCIÓN DE ATRIBUTOS Y FALLBACKS
    // =========================================================================
    const coverBackgroundType = activeThemeData?.coverBackgroundType || DEFAULT_COVER_CONFIGURATION.coverBackgroundType;
    const coverBackgroundColor = activeThemeData?.coverBackgroundColor || DEFAULT_COVER_CONFIGURATION.coverBackgroundColor;
    const coverBackgroundUrl = activeThemeData?.coverBackgroundUrl ? resolveMediaUrl(activeThemeData.coverBackgroundUrl) : '';
    const coverTextColor = activeThemeData?.coverTextColor || DEFAULT_COVER_CONFIGURATION.coverTextColor;
    const coverTitleText = activeThemeData?.coverTitle || DEFAULT_COVER_CONFIGURATION.coverTitle;
    const brandLogoUrl = activeThemeData?.brandLogoUrl ? resolveMediaUrl(activeThemeData.brandLogoUrl) : '';
    const sponsorLogoUrl = activeThemeData?.sponsorLogoUrl ? resolveMediaUrl(activeThemeData.sponsorLogoUrl) : '';

    // =========================================================================
    // 3. RENDERIZADO DEL FONDO DINÁMICO (ZERO-CANVAS GPU COMPOSITED)
    // =========================================================================
    const renderDynamicBackgroundLayer = () => {
        if (coverBackgroundType === 'video' && coverBackgroundUrl) {
            return (
                <video
                    src={coverBackgroundUrl}
                    className="buzzer-screen-cover__background-video"
                    autoPlay
                    loop
                    muted
                    playsInline
                />
            );
        }

        if (coverBackgroundType === 'image' && coverBackgroundUrl) {
            return (
                <div
                    className="buzzer-screen-cover__background-image"
                    style={{ backgroundImage: `url(${coverBackgroundUrl})` }}
                />
            );
        }

        return (
            <div
                className="buzzer-screen-cover__background-color"
                style={{ backgroundColor: coverBackgroundColor }}
            />
        );
    };

    // =========================================================================
    // 4. RENDERIZADO PRINCIPAL DE LA PORTADA (100% LIBRE DE CÓDIGOS QR)
    // =========================================================================
    return (
        <main
            className="buzzer-screen-cover"
            role="region"
            aria-label="Portada Promocional Buzzer Battle - De barrio en barrio"
        >
            {/* Capa de Fondo Dinámico */}
            {renderDynamicBackgroundLayer()}

            {/* Capa de Viñeta y Filtro de Contraste GPU */}
            <div className="buzzer-screen-cover__vignette-overlay" />

            {/* Contenido Central Flotante */}
            <div className="buzzer-screen-cover__content-wrapper">
                {/* Contenedor de Logotipos (Club / Patrocinador) */}
                <div className="buzzer-screen-cover__logos-container">
                    {sponsorLogoUrl && (
                        <div className="buzzer-screen-cover__logo-box buzzer-screen-cover__logo-box--sponsor">
                            <img
                                src={sponsorLogoUrl}
                                alt="Patrocinador Oficial"
                                className="buzzer-screen-cover__sponsor-logo"
                            />
                        </div>
                    )}

                    {brandLogoUrl ? (
                        <div className="buzzer-screen-cover__logo-box buzzer-screen-cover__logo-box--brand">
                            <img
                                src={brandLogoUrl}
                                alt="Marca / Club"
                                className="buzzer-screen-cover__brand-logo"
                            />
                        </div>
                    ) : (
                        <div className="buzzer-screen-cover__logo-box buzzer-screen-cover__logo-box--brand">
                            <img
                                src="/img/page/adsl-five.png"
                                alt="Logotipo Dorados"
                                className="buzzer-screen-cover__brand-logo"
                            />
                        </div>
                    )}
                </div>

                {/* Subtítulo Presenta */}
                <span
                    className="buzzer-screen-cover__presenta-badge"
                    style={{ color: coverTextColor }}
                >
                    PRESENTA
                </span>

                {/* Título Principal de la Dinámica con Animación Flotante y Brillo Neón */}
                <h1
                    className="buzzer-screen-cover__hero-title"
                    style={{ color: coverTextColor }}
                >
                    {coverTitleText}
                </h1>

                {/* Subtítulo de Arena */}
                {/* <p
                    className="buzzer-screen-cover__hero-subtitle"
                    style={{ color: coverTextColor }}
                >
                    ¡BATALLA DE BUZZERS EN VIVO!
                </p> */}
            </div>

            {/* Pie de Página Corporativo con Créditos */}
            <footer
                className="buzzer-screen-cover__footer-credits"
                style={{ color: coverTextColor }}
            >
                Hecho con ♥ por Interaktia
            </footer>
        </main>
    );
};

BuzzerBattleCover.propTypes = {
    payload: PropTypes.shape({
        type: PropTypes.string,
        state: PropTypes.object,
        data: PropTypes.object,
        payload: PropTypes.object,
        theme: PropTypes.object
    })
};

BuzzerBattleCover.defaultProps = {
    payload: null
};
